// Data + URL state for the vehicle catalog page. Filter/sort/page state lives in
// the query string (same convention as Trips and Stations) so a filtered view is
// linkable and the back button undoes a filter.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../lib/api';

export const PAGE_SIZE = 25;

// 'all' sends no isEligible param — the backend then returns both.
const ELIGIBILITY_PARAM = { all: undefined, eligible: true, retired: false };

// Pickable values only. 'all' is the dropdown's clear row, not an option, so it
// behaves like clearing Make/Model/Year does.
export const ELIGIBILITY_OPTIONS = [
  { key: 'eligible', label: 'Eligible' },
  { key: 'retired', label: 'Retired' },
];

// Same shape as eligibility: 'all' sends no isXl param and is the clear row.
const XL_PARAM = { all: undefined, xl: true, standard: false };

export const XL_OPTIONS = [
  { key: 'xl', label: 'XL' },
  { key: 'standard', label: 'Not XL' },
];

const errorMessage = (err) =>
  err?.response?.data?.message || err?.message || 'Something went wrong';

/**
 * Make/model/year are multi-select. In the URL they're one repeated key
 * (`?make=Honda&make=Toyota`) rather than a comma-joined value, because a make
 * or model is free text and may contain a comma. Same encoding the API takes.
 */
const asList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/**
 * Separator for the content-based effect keys below. NUL as an explicit escape,
 * not a space or comma: ['Land', 'Rover'] and ['Land Rover'] would collide on
 * those, and a collision means a selection change that never refetches.
 */
const KEY_SEP = '\u0000';

/** Only these keys live in the URL; everything else is transient. */
const buildSearch = ({ q, eligibility, xl, make, model, year, sortBy, sortDir, page, vehicleId }) => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (eligibility && eligibility !== 'all') params.set('eligibility', eligibility);
  if (xl && xl !== 'all') params.set('xl', xl);
  asList(make).forEach((v) => params.append('make', v));
  asList(model).forEach((v) => params.append('model', v));
  asList(year).forEach((v) => params.append('year', v));
  if (sortBy) params.set('sortBy', sortBy);
  if (sortBy && sortDir) params.set('sortDir', sortDir);
  if (page > 0) params.set('page', page + 1);
  if (vehicleId) params.set('vehicleId', vehicleId);
  return params.toString();
};

const useVehicleCatalog = () => {
  const history = useHistory();
  // Parsed here rather than with the shared useQuery hook: that one flattens the
  // query string to one value per key, which would drop all but the last make.
  const { search } = useLocation();

  const params = useMemo(() => new URLSearchParams(search), [search]);

  /**
   * The array filters are keyed by their CONTENTS, then re-derived from that key.
   * `getAll` hands back a fresh array on every render, and `state` is rebuilt
   * whenever any part of the URL changes — so a request keyed on those arrays
   * refetched the whole list on a change that can't affect it, like opening or
   * closing the detail panel (?vehicleId=). Going through the key means the array
   * identity only changes when the selection really did.
   */
  const makeKey = params.getAll('make').join(KEY_SEP);
  const modelKey = params.getAll('model').join(KEY_SEP);
  const yearKey = params.getAll('year').join(KEY_SEP);
  const make = useMemo(() => (makeKey ? makeKey.split(KEY_SEP) : []), [makeKey]);
  const model = useMemo(() => (modelKey ? modelKey.split(KEY_SEP) : []), [modelKey]);
  const year = useMemo(() => (yearKey ? yearKey.split(KEY_SEP) : []), [yearKey]);

  const state = useMemo(() => {
    const eligibility = params.get('eligibility');
    const xl = params.get('xl');
    return {
      q: params.get('q') || '',
      eligibility: ELIGIBILITY_OPTIONS.some((o) => o.key === eligibility) ? eligibility : 'all',
      xl: XL_OPTIONS.some((o) => o.key === xl) ? xl : 'all',
      make,
      model,
      year,
      sortBy: params.get('sortBy') || '',
      sortDir: params.get('sortDir') === 'desc' ? 'desc' : 'asc',
      // 1-based in the URL (human-facing), 0-based everywhere in code.
      page: Math.max(0, (parseInt(params.get('page'), 10) || 1) - 1),
      vehicleId: params.get('vehicleId') || '',
    };
  }, [params, make, model, year]);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({ makes: [], models: [], years: [] });

  // Local mirror of the search box so typing stays responsive; the URL (and so
  // the request) is only written on the debounce.
  const [queryInput, setQueryInput] = useState(state.q);
  const urlQuery = state.q;
  useEffect(() => {
    setQueryInput(urlQuery);
  }, [urlQuery]);

  /**
   * Any change other than paging resets to page 1 — staying on page 7 of a
   * result set that just shrank to two pages shows an empty grid.
   */
  const update = useCallback(
    (changes, { replace = false, keepPage = false } = {}) => {
      const next = { ...state, ...changes };
      if (!keepPage && !('page' in changes)) {
        next.page = 0;
      }
      const method = replace ? history.replace : history.push;
      method({ search: buildSearch(next) });
    },
    [state, history],
  );

  useEffect(() => {
    if (queryInput === urlQuery) return;
    const t = setTimeout(() => {
      // replace, not push: typing 8 characters shouldn't leave 8 history entries.
      update({ q: queryInput }, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [queryInput, urlQuery, update]);

  // Out-of-order guard: a slow page-1 response must not overwrite page 2.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const res = await api.vehicles.getVehicles({
        q: state.q || undefined,
        isEligible: ELIGIBILITY_PARAM[state.eligibility],
        isXl: XL_PARAM[state.xl],
        make: state.make,
        model: state.model,
        year: state.year,
        sortBy: state.sortBy || undefined,
        sortDir: state.sortBy ? state.sortDir : undefined,
        offset: state.page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      if (id !== requestId.current) return;
      setRows(res.data);
      setCount(res.count);
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(errorMessage(err));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // make/model/year are the content-keyed arrays above, so this is stable
    // across URL changes that don't touch the filters.
  }, [
    state.q,
    state.eligibility,
    state.xl,
    state.make,
    state.model,
    state.year,
    state.sortBy,
    state.sortDir,
    state.page,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Dropdown options cascade off the selected makes/models, so they reload when
  // either changes — and only then, for the same reason as above.
  useEffect(() => {
    let live = true;
    api.vehicles
      .getFilterOptions({ make: state.make, model: state.model })
      .then((res) => live && setOptions(res))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [state.make, state.model]);

  /** Clicking a sorted column flips direction; a new column starts ascending. */
  const toggleSort = useCallback(
    (field) =>
      update({
        sortBy: field,
        sortDir: state.sortBy === field && state.sortDir === 'asc' ? 'desc' : 'asc',
      }),
    [state.sortBy, state.sortDir, update],
  );

  // Patch the row in place rather than refetching, so flipping a switch doesn't
  // make the grid blink. Exception: an edit that changes the sorted column's
  // value could move the row to another page, so reload in that case.
  const save = useCallback(
    async (id, patch) => {
      try {
        const updated = await api.vehicles.updateVehicle(id, patch);
        const sortedFieldChanged = state.sortBy && state.sortBy in patch;
        if (sortedFieldChanged) {
          await load();
        } else {
          setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
        }
        setError(null);
        return { success: true, vehicle: updated };
      } catch (err) {
        const message = errorMessage(err);
        setError(message);
        return { success: false, message };
      }
    },
    [state.sortBy, load],
  );

  const create = useCallback(
    async (input) => {
      try {
        const vehicle = await api.vehicles.createVehicle(input);
        setError(null);
        await load();
        return { success: true, vehicle };
      } catch (err) {
        const message = errorMessage(err);
        setError(message);
        return { success: false, message };
      }
    },
    [load],
  );

  // One per active filter, not per selected value — the badge counts how many
  // filters are narrowing the list, so three makes is still "1".
  const activeFilterCount =
    (state.eligibility !== 'all' ? 1 : 0) +
    (state.xl !== 'all' ? 1 : 0) +
    (state.make.length ? 1 : 0) +
    (state.model.length ? 1 : 0) +
    (state.year.length ? 1 : 0);

  return {
    ...state,
    queryInput,
    setQueryInput,
    rows,
    count,
    loading,
    error,
    setError,
    options,
    activeFilterCount,
    update,
    toggleSort,
    setPage: (page) => update({ page }, { keepPage: true }),
    // Changing the makes clears the models and years under them. The options
    // below cascade off this selection, so a kept-around model=Civic under
    // make=Ford would just return nothing.
    setMakes: (make) => update({ make, model: [], year: [] }),
    setModels: (model) => update({ model, year: [] }),
    setYears: (year) => update({ year }),
    setEligibility: (eligibility) => update({ eligibility }),
    setXl: (xl) => update({ xl }),
    clearFilters: () => update({ eligibility: 'all', xl: 'all', make: [], model: [], year: [] }),
    select: (vehicleId) => update({ vehicleId }, { keepPage: true }),
    save,
    create,
    reload: load,
  };
};

export default useVehicleCatalog;
